/**
 * Reference letters for a current or former lab member, written by the PI: answer a short form (prefilled
 * with what Otable knows about the member, the lab and the PI), turn it into a letter with the template,
 * then edit the text freely in the editor. Letters are saved, private to the PI, and download as Word.
 * Backend: /consortium/labs/<id>/letters/ (consortium/lab/viewsets.py); template: ./referenceLetter.js.
 */
import React, { useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Grid, IconButton, MenuItem, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import { Delete as DeleteIcon, Description as LetterIcon, Download as DownloadIcon, ContentCopy as CopyIcon } from '@mui/icons-material';
import { Document, Packer } from 'docx';
import { saveAs } from 'file-saver';
import { axiosService } from 'shared/helpers/axios';
import { useSnackbar } from 'shared/contexts/SnackbarContext';
import { markdownToDocxParagraphs } from 'shared/utils/richTextMarkdown';
import SlateEditor from 'components/social/profile/reflections/SlateEditor';
import { LEVELS, PRONOUNS, RANKINGS, RELATIONSHIPS, STRENGTHS, buildLetter, emptyForm } from './referenceLetter';

const fileName = (title, name) => `${(title || `Reference letter ${name}`).replace(/[^\w-]+/g, '_').slice(0, 60)}.docx`;

function LetterForm({ form, set }) {
  const f = (key) => ({ value: form[key] ?? '', onChange: (e) => set({ [key]: e.target.value }), fullWidth: true, size: 'small' });
  const heading = (t) => <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, mt: 1 }}>{t}</Typography>;
  return (
    <Stack spacing={1.75} sx={{ pt: 1 }}>
      {heading('The letter')}
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6}><TextField label="Addressed to" placeholder="Selection Committee" {...f('recipient')} /></Grid>
        <Grid item xs={12} sm={6}><TextField label="Recommended for" placeholder="the PhD programme in Ecology at ETH Zürich" {...f('purpose')} /></Grid>
      </Grid>

      {heading('The candidate')}
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={5}><TextField label="Full name" {...f('name')} /></Grid>
        <Grid item xs={6} sm={3}><TextField label="First name in the text" {...f('first_name')} /></Grid>
        <Grid item xs={6} sm={4}>
          <TextField select label="Pronouns" {...f('pronouns')}>
            {Object.entries(PRONOUNS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={5}><TextField label="Role in the lab" placeholder="PhD student" {...f('role')} /></Grid>
        <Grid item xs={6} sm={2}><TextField label="From" type="number" {...f('start_year')} /></Grid>
        <Grid item xs={6} sm={2}><TextField label="Until" type="number" disabled={!form.is_alumni} {...f('end_year')} /></Grid>
        <Grid item xs={12} sm={3}>
          <FormControlLabel control={<Switch checked={!!form.is_alumni} onChange={(e) => set({ is_alumni: e.target.checked })} />}
            label="Former member" />
        </Grid>
        {form.is_alumni && <Grid item xs={12}><TextField label="Where they are now" placeholder="a postdoc at ETH Zürich" {...f('current_position')} /></Grid>}
        <Grid item xs={12}>
          <TextField select label="You know them as" {...f('relationship')}>
            {RELATIONSHIPS.map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
          </TextField>
        </Grid>
      </Grid>

      {heading('Their work')}
      <TextField label="What they worked on" placeholder="the role of root exudates in shaping the soil microbiome" multiline minRows={2} {...f('research')} />
      <TextField label="Achievements (one per line)" placeholder={'a first-author paper in ISME J\nthe best poster award at ISME 2024'} multiline minRows={3} {...f('achievements')} />
      <Autocomplete multiple freeSolo options={[]} value={form.skills || []} onChange={(_e, v) => set({ skills: v })}
        renderTags={(v, getTagProps) => v.map((t, i) => <Chip size="small" label={t} {...getTagProps({ index: i })} />)}
        renderInput={(params) => <TextField {...params} size="small" label="Skills and techniques" helperText="From their profile where available; press Enter to add more" />} />

      {heading('As a person and as a researcher')}
      <Box display="flex" gap={0.75} flexWrap="wrap">
        {STRENGTHS.map(([k, label]) => {
          const on = (form.strengths || []).includes(k);
          return <Chip key={k} label={label} onClick={() => set({ strengths: on ? form.strengths.filter((x) => x !== k) : [...(form.strengths || []), k] })}
            color={on ? 'primary' : 'default'} variant={on ? 'filled' : 'outlined'} />;
        })}
      </Box>
      <TextField label="A personal note (optional)" placeholder="Elif organised our lab's journal club for three years." multiline minRows={2} {...f('personal')} />
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={5}>
          <TextField select label="Compared with others you supervised" {...f('ranking')}
            InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }}>
            {RANKINGS.map(([k, l]) => <MenuItem key={k || 'none'} value={k}>{l}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={7}>
          <TextField select label="Recommendation" {...f('level')}>
            {LEVELS.map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
          </TextField>
        </Grid>
      </Grid>

      {heading('Signed by')}
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6}><TextField label="Your name" {...f('author_name')} /></Grid>
        <Grid item xs={12} sm={6}><TextField label="Your title" placeholder="Professor of Microbiology" {...f('author_title')} /></Grid>
        <Grid item xs={12} sm={4}><TextField label="Lab" {...f('lab_name')} /></Grid>
        <Grid item xs={12} sm={4}><TextField label="Institution" {...f('author_institution')} /></Grid>
        <Grid item xs={12} sm={4}><TextField label="Email" {...f('author_email')} /></Grid>
      </Grid>
    </Stack>
  );
}

export default function ReferenceLetterDialog({ open, lab, member, onClose }) {
  const { showSnackbar } = useSnackbar();
  const base = `/consortium/labs/${lab.id}/letters`;
  const [view, setView] = useState('list');                 // list | form | editor
  const [letters, setLetters] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [current, setCurrent] = useState(null);             // { id?, title, form, body }
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !member) return;
    setView('list'); setCurrent(null); setDirty(false); setError('');
    Promise.all([axiosService.get(`${base}/`, { params: { member: member.id } }), axiosService.get(`${base}/prefill/`, { params: { member: member.id } })])
      .then(([l, p]) => { setLetters(l.data); setPrefill(p.data); if (!l.data.length) startNew(p.data); })
      .catch(() => setError('The letters could not be loaded.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, member?.id]);

  const startNew = (pre = prefill) => { setCurrent({ title: '', form: emptyForm(pre), body: '' }); setView('form'); setDirty(false); };
  const setForm = (patch) => { setCurrent((c) => ({ ...c, form: { ...c.form, ...patch } })); setDirty(true); };

  const generate = () => {
    if (current.body && !window.confirm('Make the letter again from the form? Your edits in the editor will be replaced.')) return;
    const body = buildLetter(current.form);
    setCurrent((c) => ({ ...c, body, title: c.title || (c.form.purpose ? `For ${c.form.purpose}` : `Reference letter, ${c.form.name}`).slice(0, 200) }));
    setView('editor'); setDirty(true);
  };

  const save = async () => {
    setSaving(true); setError('');
    try {
      const payload = { member: member.id, title: current.title, form: current.form, body: current.body };
      const res = current.id ? await axiosService.patch(`${base}/${current.id}/`, payload) : await axiosService.post(`${base}/`, payload);
      setCurrent(res.data); setDirty(false);
      setLetters((ls) => [res.data, ...(ls || []).filter((x) => x.id !== res.data.id)]);
      showSnackbar('Letter saved', 'success');
    } catch {
      setError('The letter could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (letter) => {
    if (!window.confirm(`Delete "${letter.title || 'this letter'}"?`)) return;
    await axiosService.delete(`${base}/${letter.id}/`);
    setLetters((ls) => ls.filter((x) => x.id !== letter.id));
  };

  const download = async () => {
    const doc = new Document({ sections: [{ children: markdownToDocxParagraphs(current.body || '') }] });
    saveAs(await Packer.toBlob(doc), fileName(current.title, member.display_name));
  };

  const close = () => {
    if (dirty && !window.confirm('Close without saving the letter?')) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
        <LetterIcon color="primary" /> Reference letter · {member?.display_name}
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!letters && !error && <Box display="flex" justifyContent="center" py={5}><CircularProgress /></Box>}

        {letters && view === 'list' && (
          <Stack spacing={1}>
            <Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 1 }}>
              Your letters for {member.display_name}. Only you see them.
            </Typography>
            {letters.map((l) => (
              <Box key={l.id} display="flex" alignItems="center" gap={1} sx={{ p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
                <Box flex={1} minWidth={0}>
                  <Typography sx={{ fontWeight: 600 }} noWrap>{l.title || 'Untitled letter'}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Edited {new Date(l.updated).toLocaleDateString()}</Typography>
                </Box>
                <Button size="small" onClick={() => { setCurrent(l); setView('editor'); setDirty(false); }}>Open</Button>
                <Tooltip title="Delete"><IconButton size="small" onClick={() => remove(l)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
              </Box>
            ))}
          </Stack>
        )}

        {current && view === 'form' && <LetterForm form={current.form} set={setForm} />}

        {current && view === 'editor' && (
          <Stack spacing={1.5}>
            <TextField label="Letter name (for your list)" size="small" value={current.title}
              onChange={(e) => { setCurrent((c) => ({ ...c, title: e.target.value.slice(0, 200) })); setDirty(true); }} />
            <SlateEditor value={current.body} richText height={460}
              onChange={(v) => { if ((v || '') !== current.body) { setCurrent((c) => ({ ...c, body: v || '' })); setDirty(true); } }} />
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {view === 'list' && <Button onClick={() => startNew()} variant="contained" disabled={!prefill}>New letter</Button>}
        {view === 'form' && (
          <>
            {letters?.length > 0 && <Button onClick={() => setView('list')} sx={{ mr: 'auto' }}>All letters</Button>}
            {current?.body && <Button onClick={() => setView('editor')}>Back to the letter</Button>}
            <Button variant="contained" onClick={generate} disabled={!current?.form?.name?.trim()}>Write the letter</Button>
          </>
        )}
        {view === 'editor' && (
          <>
            <Button onClick={() => setView('list')} sx={{ mr: 'auto' }}>All letters</Button>
            <Button onClick={() => setView('form')}>Form</Button>
            <Tooltip title="Copy the text"><IconButton onClick={() => { navigator.clipboard?.writeText(current.body); showSnackbar('Copied', 'success'); }}><CopyIcon fontSize="small" /></IconButton></Tooltip>
            <Button startIcon={<DownloadIcon />} onClick={download}>Download</Button>
            <Button variant="contained" onClick={save} disabled={saving || !dirty}>{saving ? <CircularProgress size={18} /> : 'Save'}</Button>
          </>
        )}
        <Button onClick={close}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
